import {
  isLauncherJourney,
  type Journey as JourneyV3
} from '@epilot/journey-logic-commons'
import { up } from '@epilot/journey-logic-commons/src/schemas/v4'
import { LOGIC_ACTION } from '@epilot/journey-logic-commons/src/types/next'
import type {
  Block,
  Step,
  StepId,
  BlockId,
  Journey
} from '@epilot/journey-logic-commons/src/types/next'

import type { JourneyChangedAction } from '../types/actions'
import { APP_ERROR } from '../types/enums'

import { actionFactory } from './factory'
import { setAppError } from './setAppError'

/**
 * Responsible for processing a Journey config after fetching from the API or receiving it via
 * event in case of a preview session.
 */
export const journeyChanged = actionFactory<JourneyChangedAction>(
  (state, { journey }) => {
    const castedJourney = journey as JourneyV3
    const id = journey.journeyId

    if (!id) {
      return void setAppError(
        {
          error: APP_ERROR.JOURNEY_NOT_FOUND,
          reason: 'Journey Id is missing.'
        },
        state
      )
    }

    const nextJourney = trySchemaUp(castedJourney)

    const previousDesign = state.journeys?.[id]?.design

    state.journeys[id] = {
      originalJourney: castedJourney,
      journey: nextJourney,
      design: previousDesign, // keep the previous, otherwise competes with `designChanged` action
      displayConditionBlocks: inferDisplayConditionBlocks(nextJourney),
      blocksMap: computeBlocksMap(nextJourney?.steps, nextJourney?.blocks)
    }

    // caches the launcher-type check for further use
    if (state.journeys[id].journey === undefined) {
      state.journeys[id].isLauncherType = isLauncherJourney(castedJourney.steps)
    }
  },
  'journeyChanged'
)

/**
 * Safely attempts to transform the Journey schema from v3 to v4.
 * If the transformation fails, it returns undefined and logs the error.
 */
export const trySchemaUp = (journey: JourneyV3): Journey | undefined => {
  try {
    return up(journey)
  } catch (originalError) {
    const error = new Error(`Error transforming Journey schema`)

    error.cause = originalError

    // eslint-disable-next-line no-console
    console.error(error, { journeyId: journey.journeyId })

    return undefined
  }
}

/**
 * Parses through logics to infer which blocks have associated display conditions.
 * Returns a set of BlockIds that have one.
 */
export const inferDisplayConditionBlocks = (
  journey: Journey | undefined
): Set<BlockId> => {
  const set = new Set<BlockId>()

  if (journey && journey?.logics) {
    for (const logic of Object.values(journey.logics)) {
      if (logic.action === LOGIC_ACTION.DISPLAY_CONDITION) {
        set.add(logic.settings.targetBlock)
      }
    }
  }

  return set
}

export const computeBlocksMap = (
  steps: Record<StepId, Step> | undefined = {},
  blocks: Record<BlockId, Block> | undefined = {}
): Map<Step['orderIndex'], Map<Block['name'], Block>> => {
  const map: ReturnType<typeof computeBlocksMap> = new Map()

  for (const block of Object.values(blocks)) {
    const step = steps[block.stepId]
    const innerMap = map.get(step.orderIndex) ?? new Map<Block['name'], Block>()

    innerMap.set(block.name, block)

    if (!map.has(step.orderIndex)) {
      map.set(step.orderIndex, innerMap) // assigns
    }
  }

  return map
}
